import { motion } from 'framer-motion';
import { ChevronDown, Car, HandCoins, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

// Real dealership garage image
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_dealer-manager-1/artifacts/tpc7k04l_ChatGPT%20Image%20Jan%2025%2C%202026%2C%2008_48_53%20PM.png';

const Hero = () => {
  return (
    <section
      className="relative h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="CMAC & Son Autos forecourt with quality used cars"
          className="w-full h-full object-cover object-center md:object-[center_40%]"
          data-testid="hero-background-image"
          loading="eager"
          fetchPriority="high"
        />
        {/* Dark overlay for text contrast */}
        <div className="absolute inset-0 bg-black/50" />
        {/* Gradient overlays for depth and readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#020405]/40 via-transparent to-[#020405]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020405]/70 via-[#020405]/30 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#48CCCD] font-sans text-xs uppercase tracking-[0.3em] mb-6"
            data-testid="hero-subtitle"
          >
            Buy • Sell • Service
          </motion.p>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] leading-[1.1] tracking-tight mb-8"
            data-testid="hero-title"
          >
            CMAC <span className="text-[#48CCCD]">&</span> SON
            <br />
            <span className="text-[#94A3B8]">AUTOS</span>
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="font-sans text-base md:text-lg text-[#94A3B8] max-w-xl leading-relaxed mb-10"
            data-testid="hero-description"
          >
            Your trusted partner for buying quality used cars, selling your vehicle, 
            or keeping it running smoothly with our expert repair services.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            {/* Primary CTA - Browse Cars */}
            <Link
              to="/cars"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-[#48CCCD] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors duration-300"
              data-testid="hero-cta-browse"
            >
              <Car size={18} strokeWidth={2} />
              Browse Used Cars
            </Link>
            
            {/* Secondary CTA - Sell Your Car */}
            <Link
              to="/sell-your-car"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-transparent border-2 border-white/30 text-white font-bold uppercase tracking-widest text-xs hover:border-[#48CCCD] hover:text-[#48CCCD] transition-colors duration-300"
              data-testid="hero-cta-sell"
            >
              <HandCoins size={18} strokeWidth={2} />
              Sell Your Car
            </Link>
            
            {/* Tertiary CTA - Book an Appointment */}
            <Link
              to="/repairs"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 bg-transparent border-2 border-white/20 text-white/80 font-bold uppercase tracking-widest text-xs hover:border-[#48CCCD] hover:text-[#48CCCD] transition-colors duration-300"
              data-testid="hero-cta-repairs"
            >
              <Calendar size={18} strokeWidth={2} />
              Book an Appointment
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        data-testid="scroll-indicator"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center gap-2 cursor-pointer"
        >
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-sans">
            Scroll
          </span>
          <ChevronDown size={20} className="text-[#48CCCD]" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
